import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/cn';

export const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-colors [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        soft: 'border-transparent bg-brand-soft text-brand-text',
        outline: 'border-border text-foreground',
        success: 'border-transparent bg-success-soft text-success-text',
        warning: 'border-transparent bg-warning-soft text-warning-text',
        destructive: 'border-transparent bg-destructive-soft text-destructive-text',
        info: 'border-transparent bg-info-soft text-info-text',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';
  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/** Compact numeric counter used in navigation (99+ cap). */
export function CounterBadge({
  value,
  className,
  max = 99,
}: {
  value: number;
  className?: string;
  max?: number;
}) {
  if (value <= 0) return null;
  return (
    <span
      data-slot="counter-badge"
      className={cn(
        'inline-flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-brand px-1 text-[0.6875rem] leading-none font-semibold text-brand-foreground tabular-nums',
        className,
      )}
    >
      {value > max ? `${max}+` : value}
    </span>
  );
}
